// Admin controls for a participant's acceptance invitation (dark staff UI).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminUpdateInvitation, adminResendInvitation, errText } from "@/lib/api";
import { fmtDate, goldBtnCls, ghostBtnCls } from "./constants";
import { CREATORS } from "@/data/characters";

const LANE_OPTIONS = CREATORS.map((c) => c.lane);
const MISSIONS = [
  { number: "001", title: "The Founding Cohort" },
  { number: "002", title: "Color Riot" },
  { number: "003", title: "Street Press" },
];
const inputCls = "w-full border border-silver/30 bg-onyx px-2 py-1.5 font-body text-xs text-cream outline-none focus:border-gold placeholder:text-silver/40";

export const InvitationPanel = ({ record, onUpdated }) => {
  const inv = record.participant_invitation;
  const [form, setForm] = useState({ orientation_date: "", onboarding_deadline: "", primary_lane: "", profile_public_status: "hidden", mission_assignments: [] });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    setForm({
      orientation_date: inv?.orientation_date || "",
      onboarding_deadline: inv?.onboarding_deadline || "",
      primary_lane: inv?.primary_lane || "",
      profile_public_status: inv?.profile_public_status || "hidden",
      mission_assignments: inv?.mission_assignments || [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id, inv?.orientation_date, inv?.onboarding_deadline, inv?.primary_lane, inv?.profile_public_status, JSON.stringify(inv?.mission_assignments)]);

  if (!inv) return null;

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateInvitation(record.id, form);
      toast.success("Invitation details saved");
      onUpdated({ id: record.id });
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    if (!window.confirm("Resend the acceptance invitation email to this applicant?")) return;
    setResending(true);
    try {
      await adminResendInvitation(record.id);
      toast.success("Invitation email resent");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setResending(false);
    }
  };

  const laneOptions = form.primary_lane && !LANE_OPTIONS.includes(form.primary_lane)
    ? [form.primary_lane, ...LANE_OPTIONS]
    : LANE_OPTIONS;

  return (
    <div className="mt-6 border border-gold/40 bg-onyx p-4" data-testid="invitation-panel">
      <p className="mb-2 font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Acceptance Invitation</p>
      <div className="space-y-1 font-mono text-xs text-silver">
        <p data-testid="invitation-member-number"><span className="text-silver/50">MEMBER #:</span> <strong className="text-gold">{inv.member_number || "—"}</strong></p>
        <p data-testid="invitation-opened"><span className="text-silver/50">OPENED:</span> <strong className="text-cream">{inv.invitation_opened_at ? fmtDate(inv.invitation_opened_at) : "Not yet opened"}</strong></p>
        <p data-testid="invitation-accepted"><span className="text-silver/50">PLACE ACCEPTED:</span> <strong className="text-cream">{inv.place_accepted_at ? fmtDate(inv.place_accepted_at) : "Not yet"}</strong></p>
        <p data-testid="invitation-onboarding"><span className="text-silver/50">ONBOARDING:</span> <strong className="text-cream">{inv.onboarding_status}</strong></p>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <div>
          <label className="mb-1 block font-mono text-[10px] font-bold uppercase text-silver/50">Lane assignment</label>
          <select value={form.primary_lane} onChange={(e) => setForm((f) => ({ ...f, primary_lane: e.target.value }))}
            data-testid="invitation-lane-select" className={inputCls}>
            <option value="">— No lane (shows fallback) —</option>
            {laneOptions.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block font-mono text-[10px] font-bold uppercase text-silver/50">Orientation</label>
          <input value={form.orientation_date} onChange={(e) => setForm((f) => ({ ...f, orientation_date: e.target.value }))}
            placeholder="e.g. Oct 3, 2026 · Atlanta" data-testid="invitation-orientation-input" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block font-mono text-[10px] font-bold uppercase text-silver/50">Onboarding deadline</label>
          <input value={form.onboarding_deadline} onChange={(e) => setForm((f) => ({ ...f, onboarding_deadline: e.target.value }))}
            placeholder="e.g. October 15, 2026" data-testid="invitation-deadline-input" className={inputCls} />
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <p className="mb-1 font-mono text-[10px] font-bold uppercase text-silver/50">Mission assignments <span className="normal-case text-silver/35">(shows "You're deployed" on their board)</span></p>
          <div className="space-y-1" data-testid="mission-assignments">
            {MISSIONS.map((m) => (
              <label key={m.number} className="flex cursor-pointer items-center gap-2 font-body text-xs text-silver">
                <input
                  type="checkbox"
                  checked={form.mission_assignments.includes(m.number)}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    mission_assignments: e.target.checked
                      ? [...f.mission_assignments, m.number]
                      : f.mission_assignments.filter((x) => x !== m.number),
                  }))}
                  data-testid={`mission-check-${m.number}`}
                  className="h-4 w-4 accent-[#C9A227]"
                />
                <span className="font-mono text-[10px] font-bold text-gold">{m.number}</span> {m.title}
              </label>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-1 font-mono text-[10px] font-bold uppercase text-silver/50">Public cast profile <span className="normal-case text-silver/35">(requires onboarding complete)</span></p>
          <select value={form.profile_public_status} onChange={(e) => setForm((f) => ({ ...f, profile_public_status: e.target.value }))}
            data-testid="profile-public-select" className={inputCls}>
            <option value="hidden">Hidden — not shown on The Cast</option>
            <option value="public">Public — shown in Founding Cohort directory</option>
          </select>
          <p className="mt-1 font-body text-[10px] leading-relaxed text-silver/40">
            Their name display follows the public-name permission they chose during onboarding (full / first name only / never public).
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={save} disabled={saving} data-testid="save-invitation-btn" className={goldBtnCls + " px-4 py-1.5 text-[11px]"}>
          {saving ? "Saving..." : "Save Details"}
        </button>
        <button onClick={resend} disabled={resending} data-testid="resend-invitation-btn" className={ghostBtnCls + " px-4 py-1.5 text-[11px]"}>
          {resending ? "Sending..." : "Resend Invitation Email"}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input readOnly value={inv.acceptance_url} data-testid="invitation-link-input"
          className="w-full border border-silver/30 bg-charcoal px-2 py-1.5 font-mono text-[11px] text-silver" onFocus={(e) => e.target.select()} />
        <button
          onClick={() => { navigator.clipboard?.writeText(inv.acceptance_url); toast.success("Invitation link copied"); }}
          data-testid="copy-invitation-btn"
          className={ghostBtnCls + " shrink-0 px-3 py-1.5 text-[11px]"}
        >
          Copy
        </button>
      </div>
    </div>
  );
};
